// components/masterdata/WaehrungenListe.jsx

import React, {useState, useEffect, useCallback} from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import Navbar from '../utils/Navbar';

const WaehrungenListe = () => {
  const { fetchWithAuth, accessToken } = useAuth();
  const navigate = useNavigate();

  const [waehrungen, setWaehrungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newWaehrung, setNewWaehrung] = useState({
    name: "",
    name_abk: "",
    name_foreign: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchWaehrungen = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/data/waehrungen`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Waehrungen');
      }

      const data = await response.json();
      setWaehrungen(data);      // setWaehrungen(data.results); <- mit pagination
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const handleSave = async () => {
    try {
      for (const id in editData) {
        await fetchWithAuth(`/api/data/waehrungen/${id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData[id]),
        });
      }
      setEditMode(false);
      fetchWaehrungen();
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm("Willst du diese Waehrung wirklich löschen?")) return;

    try {
      await fetchWithAuth(`/api/data/waehrungen/${id}/`, {
        method: "DELETE",
      });
      fetchWaehrungen(); // Liste neu laden
    } catch (err) {
      alert("Fehler beim Löschen: " + err.message);
    }
  };

  const handleAddWaehrung = async () => {
    if (!newWaehrung.name.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/data/waehrungen/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newWaehrung)
      });

      if (response.ok) {
        setNewWaehrung({ name: "", name_abk: "", name_foreign: "" });
        fetchWaehrungen();
      } else {
        alert('Fehler beim Hinzufügen');
      }
    } catch (err) {
      alert('Netzwerkfehler: ' + err.message);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchWaehrungen();
  }, [accessToken, fetchWaehrungen]);

  if (loading) return <div>Lade Waehrungen...</div>;
  if (error) return <div className="text-danger">Fehler: {error}</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h2 className="mb-4">Währungen verwalten</h2>

        <div className="d-flex mb-4 gap-2">
          <input
            className="form-control"
            type="text"
            placeholder="Neue Währung..."
            value={newWaehrung.name}
            onChange={(e) => setNewWaehrung({ ...newWaehrung, name: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Abkürzung"
            value={newWaehrung.name_abk}
            onChange={(e) => setNewWaehrung({ ...newWaehrung, name_abk: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Name Fremdsprache"
            value={newWaehrung.name_foreign}
            onChange={(e) => setNewWaehrung({ ...newWaehrung, name_foreign: e.target.value })}
          />
          <button className="btn btn-success" onClick={handleAddWaehrung} disabled={editMode}>
            Hinzufügen
          </button>
        </div>

        {editMode && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className="bi bi-pencil-square me-2"></i>
            Bearbeitungsmodus aktiv – Änderungen noch nicht gespeichert!
          </div>
        )}

        <table className="table table-striped">
          <thead>
            <tr>
              {editMode && <th>Aktion</th>}
              <th>ID</th>
              <th>Name</th>
              <th>Abkürzung</th>
              <th>Name in Fremdsprache</th>
            </tr>
          </thead>
          <tbody>
            {waehrungen.map((kat) => (
              <tr key={kat.id}>
                {editMode && (
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteSingle(kat.id)}
                    >
                      🗑️
                    </button>
                  </td>
                )}
                <td>{kat.id}</td>

                {/* Name */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name
                  )}
                </td>

                {/* Name Abk */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name_abk || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name_abk: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name_abk
                  )}
                </td>

                {/* Name Fremdsprache */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name_foreign || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name_foreign: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name_foreign
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {editMode ? (
            <button
              className="btn btn-warning"
              onClick={() => {
                setEditMode(false);
                setEditData({});
              }}
            >
              Abbrechen
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/objekte")}
            >
              Zurück
            </button>
          )}

          {!editMode && (
            <button className="btn btn-primary" onClick={() => {
              setEditMode(true);
              const initialData = {};
              waehrungen.forEach(k => {
                initialData[k.id] = {
                  name: k.name,
                  name_abk: k.name_abk,
                  name_foreign: k.name_foreign,
                };
              });
              setEditData(initialData);
            }}>
              Bearbeiten
            </button>
          )}

          {editMode && (
              <button className="btn btn-success" onClick={handleSave}>
                Änderungen speichern
              </button>
          )}
        </div>
      </div>
    </>
  );
};

export default WaehrungenListe;
